<script lang="ts">
  import { BUNDLED_FONTS } from '../../depth/fonts';
  import { DEFAULT_TEXT, type TextParams } from '../../depth/text3d';
  import { t } from '../../i18n/index.svelte';
  import { bakeText } from '../../scene/bake';
  import { sceneStore } from '../../scene/store.svelte';
  import type { HeightData, SceneNode, Shape, Vec3 } from '../../scene/types';
  import HeightmapPainter from './HeightmapPainter.svelte';
  import Vec3Input from './Vec3Input.svelte';

  let { node }: { node: SceneNode } = $props();

  type Field = [key: string, label: string, step: number, int?: boolean];
  const FIELDS: Partial<Record<Shape['kind'], Field[]>> = {
    sphere: [
      ['radius', 'scene.f.radius', 0.1],
      ['width_segments', 'scene.f.segments', 1, true],
      ['height_segments', 'scene.f.rings', 1, true],
    ],
    torus: [
      ['radius', 'scene.f.radius', 0.1],
      ['tube', 'scene.f.tube', 0.05],
      ['radial_segments', 'scene.f.rings', 1, true],
      ['tubular_segments', 'scene.f.segments', 1, true],
    ],
    cylinder: [
      ['radius_top', 'scene.f.radiusTop', 0.1],
      ['radius_bottom', 'scene.f.radiusBottom', 0.1],
      ['height', 'scene.f.height', 0.1],
      ['radial_segments', 'scene.f.segments', 1, true],
    ],
    plane: [
      ['width', 'scene.f.width', 0.1],
      ['height', 'scene.f.height', 0.1],
    ],
    terrain: [
      ['width', 'scene.f.width', 0.1],
      ['height', 'scene.f.height', 0.1],
      ['depth', 'scene.f.relief', 0.05],
      ['resolution', 'scene.f.resolution', 8, true],
      ['scale', 'texture.scale', 1],
      ['octaves', 'texture.octaves', 1, true],
      ['seed', 'stereo.seed', 1, true],
    ],
    heightmap: [
      ['width', 'scene.f.width', 0.1],
      ['height', 'scene.f.height', 0.1],
      ['depth', 'scene.f.relief', 0.05],
    ],
  };

  const nodes = $derived(sceneStore.current.nodes);
  const fields = $derived(FIELDS[node.shape.kind] ?? []);

  /** Ids that cannot be the parent (the node itself and its descendants). */
  const forbidden = $derived.by(() => {
    const out = new Set([node.id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const n of nodes) {
        if (n.parent && out.has(n.parent) && !out.has(n.id)) {
          out.add(n.id);
          grew = true;
        }
      }
    }
    return out;
  });

  function setShapeField(key: string, raw: string, int?: boolean) {
    const v = int ? Math.round(Number(raw)) : Number(raw);
    if (!Number.isFinite(v)) return;
    sceneStore.updateNode(node.id, (n) => ((n.shape as Record<string, unknown>)[key] = v));
  }

  function setTransform(k: 'position' | 'rotation' | 'scale', v: Vec3) {
    sceneStore.updateNode(node.id, (n) => (n.transform[k] = v));
  }

  function remove() {
    sceneStore.update((d) => {
      const gone = new Set([node.id]);
      // Children move up to the removed node's parent.
      for (const n of d.nodes) if (n.parent === node.id) n.parent = node.parent;
      d.nodes = d.nodes.filter((n) => !gone.has(n.id));
      d.timeline.tracks = d.timeline.tracks.filter((tr) => !gone.has(tr.target));
      const used = new Set(d.nodes.flatMap((n) => (n.shape.kind === 'mesh' ? [n.shape.mesh] : [])));
      d.meshes = d.meshes.filter((m) => used.has(m.id));
    });
    sceneStore.selected = null;
  }

  function duplicate() {
    const id = `${node.id}_${Math.random().toString(36).slice(2, 6)}`;
    sceneStore.update((d) => {
      const copy = structuredClone($state.snapshot(node)) as SceneNode;
      copy.id = id;
      copy.name = `${node.name || node.id} copy`;
      copy.transform.position = [
        copy.transform.position[0] + 0.5,
        copy.transform.position[1],
        copy.transform.position[2],
      ];
      d.nodes.push(copy);
    });
    sceneStore.selected = id;
  }

  // ---- text nodes: re-bake on change (debounced) ----
  const textParams = $derived(
    node.source?.kind === 'text' ? ({ ...DEFAULT_TEXT, ...node.source.text } as TextParams) : null,
  );
  let bakeTimer: ReturnType<typeof setTimeout> | undefined;
  let baking = $state(false);
  function setText(patch: Partial<TextParams>) {
    if (!textParams) return;
    const next = { ...textParams, ...patch };
    clearTimeout(bakeTimer);
    bakeTimer = setTimeout(async () => {
      baking = true;
      try {
        const mesh = await bakeText(next);
        sceneStore.update((d) => {
          const n = d.nodes.find((x) => x.id === node.id);
          if (!n || n.shape.kind !== 'mesh') return;
          const old = n.shape.mesh;
          d.meshes = d.meshes.filter((m) => m.id !== old);
          d.meshes.push(mesh);
          n.shape = { kind: 'mesh', mesh: mesh.id };
          n.source = { kind: 'text', text: { ...next } };
        });
      } finally {
        baking = false;
      }
    }, 350);
  }

  function setHeight(data: HeightData) {
    sceneStore.updateNode(node.id, (n) => {
      if (n.shape.kind === 'heightmap') n.shape.data = data;
    });
  }
</script>

<div class="props" data-testid="node-props">
  <label class="field">
    <span>{t('scene.name')}</span>
    <input
      type="text"
      value={node.name}
      onchange={(e) => sceneStore.updateNode(node.id, (n) => (n.name = (e.currentTarget as HTMLInputElement).value))}
    />
  </label>
  <label class="field">
    <span>{t('scene.parent')}</span>
    <select
      value={node.parent ?? ''}
      onchange={(e) =>
        sceneStore.updateNode(node.id, (n) => (n.parent = (e.currentTarget as HTMLSelectElement).value || null))}
    >
      <option value="">—</option>
      {#each nodes.filter((n) => !forbidden.has(n.id)) as n (n.id)}
        <option value={n.id}>{n.name || n.id}</option>
      {/each}
    </select>
  </label>

  <Vec3Input
    label={t('scene.position')}
    value={node.transform.position}
    onchange={(v) => setTransform('position', v)}
    testid="prop-position"
  />
  <Vec3Input
    label={t('scene.rotation')}
    value={node.transform.rotation}
    step={5}
    onchange={(v) => setTransform('rotation', v)}
  />
  <Vec3Input label={t('scene.scale')} value={node.transform.scale} onchange={(v) => setTransform('scale', v)} />

  {#if node.shape.kind === 'box'}
    <Vec3Input
      label={t('scene.f.size')}
      value={node.shape.size}
      onchange={(v) => sceneStore.updateNode(node.id, (n) => n.shape.kind === 'box' && (n.shape.size = v))}
    />
  {/if}
  {#each fields as [key, label, step, int] (key)}
    <label class="field inline">
      <span>{t(label as never)}</span>
      <input
        type="number"
        {step}
        value={(node.shape as unknown as Record<string, number>)[key]}
        onchange={(e) => setShapeField(key, (e.currentTarget as HTMLInputElement).value, int)}
      />
    </label>
  {/each}

  {#if textParams}
    <label class="field">
      <span>{t('text.content')}</span>
      <textarea
        rows="2"
        value={textParams.text}
        oninput={(e) => setText({ text: (e.currentTarget as HTMLTextAreaElement).value })}></textarea>
    </label>
    <label class="field inline">
      <span>{t('text.font')}</span>
      <select value={textParams.font} onchange={(e) => setText({ font: (e.currentTarget as HTMLSelectElement).value })}>
        {#each BUNDLED_FONTS as f (f.id)}<option value={f.id}>{f.name}</option>{/each}
      </select>
    </label>
    <label class="field inline">
      <span>{t('text.extrude')}</span>
      <input
        type="number"
        step="0.05"
        value={textParams.extrude}
        onchange={(e) => setText({ extrude: Number((e.currentTarget as HTMLInputElement).value) })}
      />
    </label>
    <label class="field inline">
      <span>{t('text.bevel')}</span>
      <input
        type="number"
        step="0.01"
        value={textParams.bevel}
        onchange={(e) => setText({ bevel: Number((e.currentTarget as HTMLInputElement).value) })}
      />
    </label>
    {#if baking}<p class="muted small">{t('scene.baking')}</p>{/if}
  {/if}

  {#if node.shape.kind === 'heightmap'}
    {#key node.id}
      <HeightmapPainter data={node.shape.data} onchange={setHeight} />
    {/key}
  {/if}

  <div class="row actions">
    <button onclick={duplicate}>{t('scene.duplicate')}</button>
    <button class="danger" onclick={remove} data-testid="node-delete">{t('common.remove')}</button>
  </div>
</div>

<style>
  .props {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 12px;
  }
  .field.inline {
    display: grid;
    grid-template-columns: 1fr 90px;
    align-items: center;
  }
  .field span {
    color: var(--muted);
  }
  input,
  select,
  textarea {
    padding: 3px 6px;
    font-size: 13px;
  }
  .actions {
    justify-content: space-between;
  }
  .danger:hover {
    border-color: var(--danger);
    color: var(--danger);
  }
  .small {
    font-size: 12px;
    margin: 0;
  }
</style>
